// Onboarding wizard + learning-profile API.
// Mirrors the axios+token pattern used elsewhere in the app
// (token in localStorage 'cleveroo_token', base from REACT_APP_API_URL).

import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8080';

function authHeaders() {
  const token =
    localStorage.getItem('cleveroo_token') ||
    localStorage.getItem('accessToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type Role = 'self' | 'child';
export type TargetExam = 'naplan' | 'oc' | 'selective' | 'hsc' | 'general';
export type Goal = 'prepare' | 'confidence' | 'weak_area' | 'keep_sharp';
export type Cadence = 'daily_few' | 'weekly_tests' | 'intensive';
export type Confidence = 'needs_work' | 'okay' | 'strong';

export interface LearningProfilePayload {
  role: Role;
  studentName: string;
  yearLevel: string;
  targetExam: TargetExam;
  goal: Goal;
  targetDate?: string | null; // YYYY-MM-01 or null
  focusAreas: string[];
  confidence: Record<string, Confidence>;
  cadence: Cadence;
  remindersOptIn: boolean;
}

export interface LearningProfile extends LearningProfilePayload {
  userId: string;
  recommendedBand: number | null;
  onboardingCompleted: boolean;
}

export interface RecommendedTest {
  id: string;
  title: string;
  subject: string;
  totalMarks: number;
  estimatedTime: number;
  questionCount?: number;
}

export interface RecommendedTestsResponse {
  band: number | null;
  comingSoon: boolean;
  targetExam?: TargetExam;
  focusAreas?: string[];
  tests: RecommendedTest[];
}

export async function getLearningProfile(
  studentId: string
): Promise<LearningProfile | null> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/learning-profile`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data ?? null;
}

export async function saveLearningProfile(
  studentId: string,
  payload: LearningProfilePayload
): Promise<LearningProfile> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/learning-profile`,
    payload,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export async function getRecommendedTests(
  studentId: string
): Promise<RecommendedTestsResponse> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/recommended-tests`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return (
    res.data?.data ?? { band: null, comingSoon: false, tests: [] }
  );
}

// ─── Shared wizard option data + helpers (mirrors server onboardingService) ───

export const YEAR_LEVELS: { value: string; label: string }[] = [
  { value: 'K', label: 'Kindergarten' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: `Year ${i + 1}`,
  })),
];

export const NAPLAN_YEARS = new Set(['3', '5', '7', '9']);

export const COMING_SOON_EXAMS = new Set<TargetExam>(['oc', 'selective', 'hsc']);

export function pathwaysForYear(yearLevel: string): TargetExam[] {
  switch (yearLevel) {
    case '4':
      return ['naplan', 'oc', 'general'];
    case '6':
      return ['naplan', 'selective', 'general'];
    case '10':
    case '11':
    case '12':
      return ['hsc', 'general'];
    default:
      return ['naplan', 'general'];
  }
}

export const EXAM_META: Record<
  TargetExam,
  { label: string; blurb: string }
> = {
  naplan: { label: 'NAPLAN', blurb: 'National literacy & numeracy tests' },
  oc: { label: 'Opportunity Class (OC)', blurb: 'Year 4 placement test' },
  selective: {
    label: 'Selective High School',
    blurb: 'Year 6 entry exam',
  },
  hsc: { label: 'HSC', blurb: 'Higher School Certificate' },
  general: {
    label: 'Just building skills',
    blurb: 'No specific exam — keep improving',
  },
};
